import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight, Repeat, Info } from "@phosphor-icons/react";

function roundUpTo(amount, base) {
  const n = Number(amount) || 0;
  const rem = n % base;
  if (rem === 0) return n;
  return n + (base - rem);
}

export default function AmountDialog({ open, campaign, onClose, onAttemptClose, forceClose }) {
  const nav = useNavigate();
  const presets = campaign?.preset_amounts || [];

  const [monthly, setMonthly] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(0);
  const [input, setInput] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setMonthly(false);
      const def = (presets[1]?.amount || presets[0]?.amount || 3000);
      setSelectedPreset(def);
      setInput(String(def));
      setTouched(false);
    }
  }, [open]); // eslint-disable-line

  const amount = Number(input) || 0;
  const minAmount = Number(campaign?.min_amount || 300);
  const belowMin = touched && amount > 0 && amount < minAmount;
  const empty = touched && !input.trim();

  const suggestHigher = !monthly && (!touched || amount === selectedPreset) && selectedPreset < 3000
    ? presets.find((p) => p.amount === 3000) : null;

  const isCustom = !presets.find((p) => p.amount === amount);
  const roundSuggestion = useMemo(() => {
    if (!isCustom || amount < minAmount) return null;
    const up = roundUpTo(amount, 500);
    return up > amount ? up : null;
  }, [amount, isCustom, minAmount]);

  if (!open) return null;

  const canCheckout = amount >= minAmount;

  const checkout = () => {
    if (!canCheckout) { setTouched(true); return; }
    const params = new URLSearchParams();
    params.set("amount", String(amount));
    if (monthly) params.set("monthly", "1");
    if (forceClose) forceClose();
    nav(`/donate/${campaign.slug}?${params.toString()}`);
  };

  const pickPreset = (v) => { setSelectedPreset(v); setInput(String(v)); setTouched(true); };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/55 p-0 md:p-6" onClick={onAttemptClose}>
      <div className="bg-white rounded-t-3xl md:rounded-3xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="amount-dialog">
        <div className="sticky top-0 bg-white border-b border-[#DCE5D4] px-4 md:px-6 py-3 flex items-center justify-between z-10">
          <h3 className="font-display font-bold text-lg">Donate Now</h3>
          <button onClick={onAttemptClose} className="p-1.5 rounded-full hover:bg-[#EEF4E9]" data-testid="close-amount-dialog"><X size={18} /></button>
        </div>

        <div className="px-4 md:px-6 pt-3 pb-4 space-y-3">
          {campaign?.allow_monthly && (
            <div className="grid grid-cols-2 rounded-xl bg-[#EEF4E9] p-1 text-sm font-semibold">
              <button type="button" onClick={() => setMonthly(false)} className={"py-2 rounded-lg transition-all " + (!monthly ? "bg-white text-[#1F2A1C] shadow-sm" : "text-[#56624F]")} data-testid="toggle-onetime">One-time</button>
              <button type="button" onClick={() => setMonthly(true)} className={"py-2 rounded-lg transition-all inline-flex items-center justify-center gap-1 " + (monthly ? "bg-white text-[#2F7A4A] shadow-sm" : "text-[#56624F]")} data-testid="toggle-monthly"><Repeat size={14} weight="fill" /> Monthly</button>
            </div>
          )}

          <p className="text-xs text-[#56624F]">&#10084;&#65039; <strong>{campaign?.donor_count || 9} kind people</strong> have supported this cause recently. Join them!</p>

          {suggestHigher && (
            <div className="bg-[#E8F0FF] border border-[#1F4FB8]/20 rounded-xl px-3 py-2 flex items-center gap-2" data-testid="upsell-banner">
              <div className="flex-1 text-xs">
                <strong>Most donors choose &#8377;{suggestHigher.amount}.</strong> Match them?
              </div>
              <button onClick={() => pickPreset(suggestHigher.amount)} className="text-xs font-semibold text-[#4FAC39] hover:underline shrink-0" data-testid="upsell-accept">Yes</button>
            </div>
          )}

          <div>
            <div className="text-xs font-semibold text-[#56624F] uppercase tracking-wider mb-1.5">{monthly ? "Choose a monthly amount" : "Choose an amount"}</div>
            <div className="grid grid-cols-2 gap-2">
              {presets.map((p) => (
                <button
                  key={p.amount}
                  onClick={() => pickPreset(p.amount)}
                  data-testid={`amount-preset-${p.amount}`}
                  className={"px-2 py-2.5 rounded-xl border text-left transition-all " + (amount === p.amount ? "border-[#4FAC39] bg-[#4FAC39] text-white" : "border-[#DCE5D4] bg-white text-[#1F2A1C] hover:border-[#4FAC39]/60")}
                >
                  <div className="font-display font-bold text-base">&#8377;{p.amount}{monthly ? <span className="text-xs font-normal opacity-80">/mo</span> : null}</div>
                  <div className={"text-[11px] leading-tight " + (amount === p.amount ? "text-white/90" : "text-[#56624F]")}>{p.label}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-[#56624F] uppercase tracking-wider">{monthly ? "Or custom ₹/mo" : "Or custom ₹"}</span>
              <input
                type="number" min={minAmount} value={input}
                onChange={(e) => { setInput(e.target.value); setTouched(true); }}
                onFocus={() => setTouched(true)}
                className={"flex-1 px-3 py-1.5 rounded-lg border outline-none bg-white text-sm " + (belowMin || empty ? "border-[#B84A34]" : "border-[#DCE5D4] focus:border-[#4FAC39]")}
                data-testid="amount-input"
              />
            </div>
            {(belowMin || empty) && (
              <div className="mt-1 text-xs text-[#B84A34] flex items-center gap-1" data-testid="min-amount-error">
                <Info size={12} weight="fill" /> Minimum &#8377;{minAmount}
              </div>
            )}
            {roundSuggestion && !monthly && (
              <button onClick={() => pickPreset(roundSuggestion)} className="mt-1.5 text-xs text-[#4FAC39] font-semibold hover:underline" data-testid="roundup-accept">
                Round up to &#8377;{roundSuggestion}?
              </button>
            )}
          </div>

          <div className="bg-[#E6F3DC] border border-[#4FAC39]/30 rounded-lg px-3 py-1.5 text-xs text-[#2F7A4A] flex items-center gap-1.5">
            <Info size={14} weight="fill" /> {monthly ? <>Cancel your monthly donation anytime. <strong>80G tax benefits</strong> apply.</> : <>Qualifies for <strong>80G tax benefits</strong></>}
          </div>

          <button onClick={checkout} disabled={!canCheckout} className="btn-primary w-full !py-3 text-sm tracking-wide uppercase disabled:opacity-60 disabled:cursor-not-allowed" data-testid="amount-checkout-btn">
            Continue &#8377;{amount || 0}{monthly ? "/mo" : ""} <ArrowRight size={14} className="inline ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
}
